import {
  BusinessChallengesEnum,
  OrganizationApplicationParameters,
} from '@platform/client/schemas';
import { FormsInputDropdown, FormsMultilineInput } from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Margaret-Yebei.svg';

type FormValues = {
  firstBusinessChallenge: BusinessChallengesEnum;
  firstBusinessChallengeDescription?: string;
};

export const challengeOptions = [
  { text: 'Product Market Fit', textValue: BusinessChallengesEnum['product-market-fit'] },
  {
    text: 'Branding And Positioning',
    textValue: BusinessChallengesEnum['branding-and-positioning'],
  },
  {
    text: 'Marketing',
    textValue: BusinessChallengesEnum['marketing'],
  },
  { text: 'Human Resources', textValue: BusinessChallengesEnum['human-resources'] },
  { text: 'Sales', textValue: BusinessChallengesEnum['sales'] },
  {
    text: 'Reporting and Communicating Impact',
    textValue: BusinessChallengesEnum['reporting-and-communicating-impact'],
  },
  { text: 'Customer Success', textValue: BusinessChallengesEnum['customer-success'] },
  { text: 'Legal', textValue: BusinessChallengesEnum['legal'] },
  {
    text: 'Fundraising Preparation',
    textValue: BusinessChallengesEnum['fundraising-preparation'],
  },
  { text: 'Business Operations', textValue: BusinessChallengesEnum['business-operations'] },
  { text: 'Accounting and Finance', textValue: BusinessChallengesEnum['accounting-and-finance'] },
];

export const OrgFirstChallengeSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey, isEditProfile = false } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const {
    handleSubmit,
    watch,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      firstBusinessChallenge: getApplicationDataByKey('firstBusinessChallenge'),
      firstBusinessChallengeDescription: getApplicationDataByKey(
        'firstBusinessChallengeDescription',
      ),
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    updateApplicationData({
      firstBusinessChallenge: data.firstBusinessChallenge,
      firstBusinessChallengeDescription: data.firstBusinessChallengeDescription,
    });
    onContinue();
  };

  const handleBack = () => {
    updateApplicationData({
      firstBusinessChallenge: watch('firstBusinessChallenge'),
      firstBusinessChallengeDescription: watch('firstBusinessChallengeDescription'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={isEditProfile}
    >
      <ApplicationPageContent
        title='What are the top 3 challenges your organization is currently working to overcome?'
        description={
          "This helps us connect you with the right skills and expertise. You'll have a chance to explain each one."
        }
      >
        <FormsInputDropdown
          control={control}
          name='firstBusinessChallenge'
          rules={{ required: 'Select a challenge your organization needs help with to continue' }}
          label='First business challenge'
          placeholder='Select one'
          items={challengeOptions}
          type='default'
          error={errors.firstBusinessChallenge}
        />
        <FormsMultilineInput
          control={control}
          label='Tell us more about this challenge'
          placeholder='Enter a description...'
          name={'firstBusinessChallengeDescription'}
          rules={{ maxLength: 500, required: 'Tell us more about this challenge to continue' }}
          formsError={errors.firstBusinessChallengeDescription}
          characterCount={500}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
